import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useCallback, useState } from 'react';
import { ProductCard } from './ProductCard';
import { trackEcommerce, trackEvent } from '../../analytics';
export const ProductCarousel = ({ products, onAddToCart, onViewProduct, sessionId, showCarouselArrows = false, carouselArrowSvg, }) => {
    const trackedProductsRef = useRef(new Set());
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const updateScrollButtons = useCallback(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        // RTL-aware scroll detection.
        // In RTL documents, scrollLeft can be 0 at the visual right end and negative
        // (or a large positive value in some browsers) at the visual left end.
        // Using Math.abs() normalises both conventions to a single positive range.
        const maxScroll = el.scrollWidth - el.clientWidth;
        const absScroll = Math.abs(el.scrollLeft);
        setCanScrollLeft(absScroll > 4);
        setCanScrollRight(absScroll < maxScroll - 4);
    }, []);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        updateScrollButtons();
        el.addEventListener('scroll', updateScrollButtons, { passive: true });
        const observer = new ResizeObserver(updateScrollButtons);
        observer.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollButtons);
            observer.disconnect();
        };
    }, [updateScrollButtons, products]);
    const scroll = useCallback((direction) => {
        const el = scrollRef.current;
        if (!el)
            return;
        const cardWidth = 212; // 200px card + 12px gap
        el.scrollBy({ left: direction === 'right' ? cardWidth * 2 : -cardWidth * 2, behavior: 'smooth' });
        // Refresh button visibility after the smooth-scroll animation finishes (~400 ms).
        setTimeout(() => updateScrollButtons(), 400);
    }, [updateScrollButtons]);
    /**
     * Track product views when products are displayed
     */
    useEffect(() => {
        if (products.length === 0)
            return;
        // Create a unique key for this set of products
        const productIds = products.map(p => String(p.id)).sort().join(',');
        // Only track if we haven't tracked this exact set of products yet
        if (trackedProductsRef.current.has(productIds))
            return;
        trackedProductsRef.current.add(productIds);
        // Track product view event with all product IDs
        trackEcommerce('product_view', products.map(product => ({
            id: product.id,
            name: product.name,
            price: typeof product.price === 'string' ? parseFloat(product.price) || 0 : product.price,
        })), {
            label: 'chat_carousel',
            custom: {
                product_count: products.length,
                product_ids: products.map(p => String(p.id)).join(','),
                ...(sessionId ? { session_id: sessionId } : {}),
            },
        });
    }, [products, sessionId]);
    /**
     * Handle product click tracking
     */
    const handleProductClick = useCallback((productId, productName) => {
        // Track product click event
        trackEvent('product_click', {
            category: 'ecommerce',
            label: 'chat_carousel',
            contentIds: [productId],
            contentName: productName,
            custom: {
                product_id: String(productId),
                product_name: productName,
                source: 'chat_carousel',
                ...(sessionId ? { session_id: sessionId } : {}),
            },
        });
        // Call the original onViewProduct callback if provided
        onViewProduct?.(productId);
    }, [onViewProduct, sessionId]);
    /**
     * Wrap onAddToCart to track add_to_cart events
     */
    const handleAddToCart = useCallback((productId, variationId) => {
        const product = products.find(p => p.id === productId);
        // Track add to cart event
        if (product) {
            trackEcommerce('add_to_cart', [{
                    id: product.id,
                    name: product.name,
                    price: typeof product.price === 'string' ? parseFloat(product.price) || 0 : product.price,
                    quantity: 1,
                }], {
                label: 'chat_carousel',
                custom: {
                    variation_id: variationId ? String(variationId) : undefined,
                    source: 'chat_carousel',
                    ...(sessionId ? { session_id: sessionId } : {}),
                },
            });
        }
        // Call the original onAddToCart callback with product name for notification
        onAddToCart(productId, variationId, product?.name);
    }, [products, onAddToCart, sessionId]);
    // Generic chevron SVGs used as fallback when showCarouselArrows=true but no custom SVG provided.
    const fallbackLeftSvg = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6" /></svg>';
    const fallbackRightSvg = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6" /></svg>';
    return (_jsxs("div", { dir: "ltr", className: "wac-relative wac-w-full wac-min-w-0 wac-max-w-full", children: [showCarouselArrows && canScrollLeft && (_jsx("button", { type: "button", onClick: () => scroll('left'), className: "wac-absolute wac-top-1/2 wac--translate-y-1/2 wac-z-10 wac-w-8 wac-h-8 wac-flex wac-items-center wac-justify-center wac-rounded-full wac-shadow-md wac-transition-opacity wac-opacity-80 hover:wac-opacity-100", style: { left: 0, background: 'var(--waa-bg-primary, #fff)', color: 'var(--waa-gray-700, #374151)' }, "aria-label": "Scroll left", dangerouslySetInnerHTML: { __html: carouselArrowSvg?.left ?? fallbackLeftSvg } })), showCarouselArrows && canScrollRight && (_jsx("button", { type: "button", onClick: () => scroll('right'), className: "wac-absolute wac-top-1/2 wac--translate-y-1/2 wac-z-10 wac-w-8 wac-h-8 wac-flex wac-items-center wac-justify-center wac-rounded-full wac-shadow-md wac-transition-opacity wac-opacity-80 hover:wac-opacity-100", style: { right: 0, background: 'var(--waa-bg-primary, #fff)', color: 'var(--waa-gray-700, #374151)' }, "aria-label": "Scroll right", dangerouslySetInnerHTML: { __html: carouselArrowSvg?.right ?? fallbackRightSvg } })), _jsx("div", { ref: scrollRef, className: "wac-w-full wac-min-w-0 wac-overflow-x-auto wac-overflow-y-hidden wac-py-3 wac-px-1", style: {
                    // No `touch-action: pan-x`: per spec a vertical drag that STARTS on a
                    // pan-x element is fully consumed, so the chat history could not be
                    // scrolled by dragging over a product carousel (#365). Default `auto`
                    // lets the browser route horizontal drags to this strip's overflow-x
                    // and vertical drags to the messages list. Likewise no
                    // onTouchMove=stopPropagation (it blocked vertical scroll-through).
                    WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin',
                }, onWheel: (e) => {
                    // Only stop propagation if scrolling horizontally
                    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                        e.stopPropagation();
                    }
                }, children: _jsx("div", { className: "wac-flex wac-gap-3 wac-flex-nowrap", style: { minWidth: 'max-content' }, children: products.map((product) => (_jsx("div", { onClick: () => handleProductClick(product.id, product.name), role: "button", tabIndex: 0, onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleProductClick(product.id, product.name);
                            }
                        }, children: _jsx(ProductCard, { id: product.id, name: product.name, price: product.price, price_formatted: product.price_formatted, image: product.image || product.image_url, url: product.url || product.permalink, type: product.type, attributes: product.attributes, variations: product.variations, variation_attributes: product.variation_attributes, tags: product.tags, on_sale: product.on_sale, average_rating: product.average_rating, review_count: product.review_count, features: product.features, badges: product.badges, review_quote: product.review_quote, promo_badge: product.promo_badge, cta_button: product.cta_button, onAddToCart: handleAddToCart }) }, product.id))) }) })] }));
};
